import { isValidEnabledFilterValue } from '@choco-admin/utils';
import React, { useState, useEffect } from 'react';

import { ChocoSelect } from '../../../../../components/FormComponents';
import { TableHeader } from '../../../../../components/Table';
import { useDebounce } from '../../../../../hooks';

interface SearchActionsBarProps {
  onFilterChange: (filters: {
    type?: string;
    enabledState?: 'all' | 'enabled' | 'disabled';
  }) => void;
  defaultSearchInputValue?: string;
  onSearchInputChange: (value: string) => void;
  currentFilters?: {
    type?: string;
    enabledState?: 'all' | 'enabled' | 'disabled';
  };
}

const chatTypeOptions = [
  { value: 'all', label: 'All Types' },
  { value: 'ordering', label: 'Ordering' },
  { value: 'support', label: 'Support' },
];

const enabledStateOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];

export const SearchActionsBar: React.FC<SearchActionsBarProps> = ({
  onFilterChange,
  defaultSearchInputValue = '',
  onSearchInputChange,
  currentFilters = { type: 'all', enabledState: 'all' },
}) => {
  const [searchInput, setSearchInput] = useState(defaultSearchInputValue);
  const debouncedSearchTerm = useDebounce(searchInput, 500); // 500ms delay

  // Effect to handle debounced search
  useEffect(() => {
    onSearchInputChange(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearchInputChange]);

  // Update local state when default value changes (e.g., from external reset)
  useEffect(() => {
    setSearchInput(defaultSearchInputValue);
  }, [defaultSearchInputValue]);

  const handleTypeChange = (value: string) => {
    onFilterChange({ type: value });
  };

  const handleEnabledStateChange = (value: string) => {
    if (isValidEnabledFilterValue(value)) {
      onFilterChange({ enabledState: value });
    }
  };

  return (
    <TableHeader
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      searchPlaceholder="Search chats, restaurants, or products..."
    >
      <div className="flex gap-4">
        <div className="w-44">
          <ChocoSelect
            placeholder="Filter by type"
            aria-label="Filter chats by type"
            options={chatTypeOptions}
            value={currentFilters.type || 'all'}
            onSelectionChange={(selectedValue) => {
              if (selectedValue) {
                handleTypeChange(selectedValue);
              }
            }}
            size="sm"
            showDescriptions={false}
          />
        </div>
        <div className="w-44">
          <ChocoSelect
            placeholder="Filter by status"
            aria-label="Filter chats by status"
            options={enabledStateOptions}
            value={currentFilters.enabledState || 'all'}
            onSelectionChange={(selectedValue) => {
              if (selectedValue) {
                handleEnabledStateChange(selectedValue);
              }
            }}
            size="sm"
            showDescriptions={false}
          />
        </div>
      </div>
    </TableHeader>
  );
};
